import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { orderAPI, clientDashboardAPI, getAdminParentPreview, clearAdminParentPreview } from '../services/api';
import { logger } from '../utils/logger';
import { hasOpenBalance } from '../utils/invoiceBalance';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Card,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Info as InfoIcon,
  GridView as DashboardIcon,
} from '@mui/icons-material';
import { LogOut } from 'lucide-react';
// Custom SVG icons
import { ReactComponent as SparklesIcon } from '../assets/icons/sparkles.svg';
import logoIcon from '../assets/icons/logo.svg';
import chartIcon from '../assets/icons/chart.svg';
import swapIcon from '../assets/icons/swap.svg';
import notebookIcon from '../assets/icons/notebook.svg';
import locationOnIcon from '../assets/icons/location_on.svg';
import shareIcon from '../assets/icons/share-2.svg';
import settingsIcon from '../assets/icons/settings-2.svg';

const drawerWidth = 240;
const headerHeight = 64;

const HIDE_SIDEBAR_ROUTES = ['/client/checkout', '/client/confirmation', '/client/product'];

const ClientNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unpaidCount, setUnpaidCount] = useState(0);
  // Admin parent preview -- read once on mount + on each location
  // change so the banner refreshes when the admin lands here from
  // Client Management's "View As" button.
  const [adminParentPreview, setAdminParentPreview] = useState(() => getAdminParentPreview());
  const profileMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setAdminParentPreview(getAdminParentPreview());
  }, [location.pathname]);

  const handleExitParentPreview = () => {
    clearAdminParentPreview();
    setAdminParentPreview(null);
    navigate('/internal/epl/client-management');
  };

  // Load unpaid invoice count for sidebar badge. Refetches on route change so
  // the badge updates after payments without a hard reload.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const dashboardResp = await clientDashboardAPI.getDashboardData();
        if (dashboardResp?.success) {
          if (!cancelled) setUnpaidCount(dashboardResp.data?.outstanding?.invoiceCount || 0);
          return;
        }

        const resp = await orderAPI.getOrders({ page: 1, limit: 100 });
        const list = resp?.success ? resp.data : [];
        const count = list.reduce((n, order) => (hasOpenBalance(order) ? n + 1 : n), 0);
        if (!cancelled) setUnpaidCount(count);
      } catch (err) {
        logger.error('Sidebar unpaid count fetch failed:', err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const hideSidebar = HIDE_SIDEBAR_ROUTES.some(route => location.pathname.startsWith(route));
  const hideOutstandingBanner = hideSidebar || location.pathname === '/client/dashboard';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    onLogout();
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return 'U';
    const first = currentUser.firstName || currentUser.name || '';
    const last = currentUser.lastName || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'U';
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon sx={{ fontSize: 22, color: 'rgba(33,33,33,0.7)' }} />, path: '/client/dashboard' },
    { text: 'Order History', iconSrc: chartIcon, path: '/client/orders' },
    { text: 'Transfers/Consignment', iconSrc: swapIcon, path: '/client/transfers' },
    { text: 'Inventory Management', iconSrc: notebookIcon, path: '/client/inventory-management' },
    { text: 'Horses', icon: <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box component="img" src={`${process.env.PUBLIC_URL}/Assets/horseVector.png`} alt="" sx={{ width: 14, height: 15, objectFit: 'contain' }} /></Box>, path: '/client/horses' },
    { text: 'My Addresses', iconSrc: locationOnIcon, path: '/client/my-shipping-address' },
    { text: 'Resources', iconSrc: shareIcon, path: '/client/resources' },
    { text: 'Settings', iconSrc: settingsIcon, path: '/client/settings' }
  ];

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f7f7f8',
      borderRight: '1px solid #ececef',
      fontFamily: 'Poppins, sans-serif',
      pt: `${headerHeight}px`,
    }}>
      {/* Shop Products Button */}
      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        <Button
          onClick={() => {
            navigate('/client/place-order');
            if (isMobile) setMobileOpen(false);
          }}
          sx={{
            width: '100%',
            bgcolor: '#009c87',
            color: '#f3f3f3',
            borderRadius: '6px',
            py: '6px',
            px: '8px',
            textTransform: 'none',
            fontFamily: 'Poppins',
            fontSize: '15px',
            fontWeight: 400,
            display: 'flex',
            gap: '4px',
            justifyContent: 'center',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: '#008574' }
          }}
        >
          <Box sx={{
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f3f3f3',
            '& path, & polygon, & circle, & rect, & line, & polyline': {
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 1.6,
              strokeLinejoin: 'round',
              strokeLinecap: 'round'
            }
          }}>
            <SparklesIcon />
          </Box>
          Shop Products
        </Button>
      </Box>

      {/* Navigation Menu + Need Help (grouped together, not pushed apart) */}
      <Box sx={{ px: 2 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: '4px' }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: '6px',
                  backgroundColor: location.pathname === item.path
                    ? '#e5e7eb'
                    : 'transparent',
                  color: '#212121',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? '#e5e7eb' : '#f7f7f8'
                  },
                  py: '8px',
                  px: '8px',
                  gap: '4px',
                  minHeight: '40px',
                  overflow: 'hidden',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'rgba(33,33,33,0.7)',
                    minWidth: 'auto',
                    mr: '4px',
                    flexShrink: 0,
                  }}
                >
                  {item.icon || <Box component="img" src={item.iconSrc} alt="" sx={{ width: 24, height: 24 }} />}
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '13px',
                          fontWeight: 400,
                          lineHeight: '18px',
                          color: 'rgba(33,33,33,0.7)',
                        }}
                      >
                        {item.text}
                      </Typography>
                      {item.path === '/client/orders' && unpaidCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#d61616',
                            color: '#ffffff',
                            fontFamily: 'Poppins',
                            fontSize: '11px',
                            fontWeight: 600,
                            lineHeight: 1,
                            px: '6px',
                            borderRadius: '6px',
                            minWidth: '20px',
                            height: '18px',
                            flexShrink: 0,
                          }}
                        >
                          {unpaidCount}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Need Help Section - immediately after nav items */}
      <Box sx={{ px: 2, pt: '12px' }}>
        <Card sx={{
          bgcolor: 'white',
          borderRadius: '6px',
          border: '1px solid #ececef',
          boxShadow: 'none',
          p: 2
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '15px',
              fontWeight: 700,
              color: '#212121',
              lineHeight: '20px',
              mb: '4px'
            }}>
              Need help?
            </Typography>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '12px',
              fontWeight: 400,
              color: 'rgba(33,33,33,0.7)',
              lineHeight: '18px'
            }}>
              Can't find something? We are always here to help.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outlined"
              onClick={() => window.open('https://www.equineperformancelabs.com/contact', '_blank')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderColor: '#ececef',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '12px',
                bgcolor: 'white',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#ececef',
                  bgcolor: 'white',
                  opacity: 0.8
                }
              }}
            >
              Help center
            </Button>
            <Button
              onClick={() => window.open('mailto:info@equineperformancelabs.com')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '4px',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: 'transparent',
                  opacity: 0.8
                }
              }}
            >
              Contact us
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Spacer to push Sign Out to bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Sign Out - matches Figma styling */}
      <Box sx={{ px: '20px', pb: 2 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '6px',
            color: 'rgba(33,33,33,0.7)',
            '&:hover': {
              backgroundColor: '#e5e7eb'
            },
            py: '8px',
            px: '8px',
            gap: '4px',
            minHeight: '40px'
          }}
        >
          <ListItemIcon
            sx={{
              color: 'rgba(33,33,33,0.7)',
              minWidth: 'auto',
              mr: '4px',
            }}
          >
            <Box component="img" src={shareIcon} alt="" sx={{ width: 24, height: 24, opacity: 0.7 }} />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              fontFamily: 'Poppins',
              fontSize: '15px',
              fontWeight: 400,
              lineHeight: '20px',
              color: 'rgba(33,33,33,0.7)'
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* Top Header Bar */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${headerHeight}px`,
        bgcolor: 'white',
        borderBottom: '1px solid #d9d9d9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, md: 4 },
        zIndex: 1300,
        fontFamily: 'Poppins, sans-serif',
      }}>
        {/* Logo */}
        <Box component="img" src={logoIcon} alt="UnisynHub" onClick={() => navigate('/client/dashboard')} sx={{ height: '35px', width: 'auto', cursor: 'pointer' }} />

        {/* User Avatar with Dropdown */}
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: '8px',
            px: 1.5,
            py: 1,
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          <Box sx={{
            width: 40,
            height: 40,
            bgcolor: '#039582',
            borderRadius: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: '16px',
              color: 'white',
              lineHeight: 1,
            }}>
              {getUserInitials()}
            </Typography>
          </Box>
          <ChevronDownIcon sx={{
            fontSize: 16,
            color: '#1e1e1e',
            transform: profileMenuOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }} />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={profileMenuOpen}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              minWidth: 220,
              fontFamily: 'Poppins',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '14px', color: '#1e1e1e' }}>
              {currentUser?.firstName || currentUser?.name || 'User'} {currentUser?.lastName || ''}
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '12px', color: '#888' }}>
              {currentUser?.email || ''}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => { setAnchorEl(null); navigate('/client/settings'); }}
            sx={{ fontFamily: 'Poppins', fontSize: '14px', py: 1.5, gap: 1.5 }}
          >
            <Box component="img" src={settingsIcon} alt="" sx={{ width: 18, height: 18 }} />
            Settings
          </MenuItem>
          <MenuItem
            onClick={() => { setAnchorEl(null); handleLogout(); }}
            sx={{ fontFamily: 'Poppins', fontSize: '14px', py: 1.5, gap: 1.5, color: '#d32f2f' }}
          >
            <LogOut size={18} color="#d32f2f" strokeWidth={1.6} />
            Sign Out
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, pt: `${headerHeight}px` }}>
        {!hideSidebar && (
          <>
            {/* Navigation Drawer */}
            <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
              {/* Mobile drawer */}
              <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: drawerWidth,
                    top: `${headerHeight}px`,
                    height: `calc(100vh - ${headerHeight}px)`,
                  }
                }}
              >
                {drawer}
              </Drawer>

              {/* Desktop drawer */}
              <Drawer
                variant="permanent"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: drawerWidth,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    zIndex: 1200,
                  }
                }}
                open
              >
                {drawer}
              </Drawer>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                position: 'fixed',
                top: headerHeight + 10,
                left: 20,
                zIndex: 1100,
                display: { md: 'none' },
                bgcolor: '#039582',
                color: 'white',
                '&:hover': { bgcolor: '#027A6B' }
              }}
            >
              <MenuIcon />
            </IconButton>
          </>
        )}

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: hideSidebar ? '100%' : { md: `calc(100% - ${drawerWidth}px)` },
            backgroundColor: 'white',
            minHeight: `calc(100vh - ${headerHeight}px)`,
          }}
        >
          <Box sx={{ p: hideSidebar ? 0 : 3 }}>
            {/* Admin parent preview banner -- shown when an INTERNAL
                admin clicked "View As" on a parent company. The banner
                makes it obvious the admin isn't viewing their own
                portal AND offers a one-click exit. Sits above the
                unpaid-balance banner so admins always see it first. */}
            {adminParentPreview && !hideSidebar && (
              <Box
                sx={{
                  mb: 3,
                  bgcolor: '#0f172a',
                  color: '#e2e8f0',
                  borderRadius: '10px',
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InfoIcon sx={{ fontSize: 20, color: '#7dd3fc' }} />
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#e2e8f0',
                    }}
                  >
                    Admin Preview — Viewing the parent admin portal for{' '}
                    <Box component="span" sx={{ color: '#7dd3fc', fontWeight: 700 }}>
                      {adminParentPreview.parentCompanyName}
                    </Box>
                  </Typography>
                </Box>
                <Button
                  onClick={handleExitParentPreview}
                  sx={{
                    textTransform: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 500,
                    fontSize: 13,
                    height: 32,
                    px: 1.5,
                    bgcolor: 'rgba(255,255,255,0.12)',
                    color: '#e2e8f0',
                    borderRadius: '6px',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  Exit Preview
                </Button>
              </Box>
            )}
            {unpaidCount > 0 && !hideOutstandingBanner && (
              <Box
                sx={{
                  mb: 3,
                  bgcolor: '#009c87',
                  color: '#ebffee',
                  borderRadius: '10px',
                  px: 3,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  flexWrap: 'wrap',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InfoIcon sx={{ fontSize: 20, color: '#ebffee' }} />
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: 1.4,
                      color: '#ebffee',
                    }}
                  >
                    Outstanding Balance Due on [{unpaidCount}] Invoice
                    {unpaidCount === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Button
                  disableElevation
                  onClick={() => navigate('/client/orders')}
                  sx={{
                    textTransform: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 400,
                    fontSize: 14,
                    height: 38,
                    px: 1.5,
                    bgcolor: 'rgba(255,255,255,0.85)',
                    color: '#1e1e1e',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#ffffff', boxShadow: 'none' },
                  }}
                >
                  Pay Balance
                </Button>
              </Box>
            )}
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientNavigation;
